/**
 * Ce qu'il y a réellement dans l'air, polluant par polluant.
 *
 * Les particules affichent la mesure au sol quand elle existe, et le modèle
 * européen sinon. Les quatre gaz ne viennent que du modèle : aucun capteur
 * citoyen ne les mesure.
 */
import { formatClock, formatConcentration } from '../../core/format';
import { median } from '../../core/geo';
import {
  LEVEL_COLOR_VARS,
  LEVEL_WORDS,
  levelIndex,
  pollutantByKey,
  type PollutantKey,
} from '../../core/pollutants';
import type { AppState } from '../../state/store';
import { freshSensors } from '../../core/verdict';
import { html, type Html } from '../html';

function row(key: PollutantKey, value: number | null, measured: boolean): Html {
  if (value === null) return html``;
  const pollutant = pollutantByKey(key);
  const level = levelIndex(value, pollutant.thresholds);

  return html`
    <li class="airrow">
      <div class="what">
        <b>${pollutant.name}</b> <span class="short">${pollutant.short}</span>
        <small>${pollutant.what}</small>
      </div>
      <div class="value" style="--level-color: var(${LEVEL_COLOR_VARS[level]})">
        <b>${formatConcentration(value)}</b><span class="unit">${pollutant.unit}</span>
        <em class="word">${LEVEL_WORDS[level]}${measured ? ', mesuré' : ''}</em>
      </div>
    </li>
  `;
}

export function airList(state: AppState, now: number): Html {
  const air = state.air;
  const fresh = freshSensors(state.sensors, now);

  const measuredPm25 = fresh.length > 0 ? median(fresh.map((s) => s.pm25)) : null;
  const withPm10 = fresh.filter((sensor) => sensor.pm10 !== null);
  const measuredPm10 =
    withPm10.length > 0 ? median(withPm10.map((sensor) => sensor.pm10!)) : null;

  const rows = [
    row('pm25', measuredPm25 ?? air?.pm25 ?? null, measuredPm25 !== null),
    row('pm10', measuredPm10 ?? air?.pm10 ?? null, measuredPm10 !== null),
    row('co', air?.co ?? null, false),
    row('o3', air?.o3 ?? null, false),
    row('no2', air?.no2 ?? null, false),
    row('so2', air?.so2 ?? null, false),
  ].filter((entry) => entry.value !== '');

  if (rows.length === 0) return html``;

  /*
   * On annonce la distance RÉELLE du capteur le plus proche.
   *
   * Le texte disait "à moins de 25 km", ce qui n'est pas garanti : l'API de
   * sensor.community interroge une boîte englobante et non un disque, donc un
   * capteur situé dans un coin peut dépasser le rayon demandé. Un relevé réel
   * annonçait 25 km pour un capteur à 29.
   */
  const nearestKm =
    fresh.length > 0 ? Math.min(...fresh.map((sensor) => sensor.distanceKm)) : null;

  const origin =
    measuredPm25 !== null && nearestKm !== null
      ? `${fresh.length} capteur${fresh.length > 1 ? 's' : ''}, le plus proche à ` +
        `${Math.round(nearestKm)} km, à ${formatClock(Math.max(...fresh.map((s) => s.at)), now)}`
      : air?.at != null
        ? `estimation européenne de ${formatClock(air.at, now)}`
        : '';

  return html`
    <section aria-labelledby="air-title">
      <h3 class="section-title" id="air-title">
        Ce qu'il y a dans l'air <span class="origin">${origin}</span>
      </h3>
      <ul class="airlist">
        ${rows}
      </ul>
    </section>
  `;
}
